import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/* ══════════════════════════════════════════════
   LANDING VARIANT F — "Poster / Neo-Brutalist"
   פוסטר ישראלי: Karantina ענק, צהוב-שחור, מדבקות עם
   צל קשיח, חותמת מסתובבת, פולארוידים נעוצים בסיכה.
   ══════════════════════════════════════════════ */

const POSTER = "'Karantina', 'Secular One', sans-serif";
const YELLOW = '#FFD60A';
const BLACK = '#111111';
const PINK = '#FF5D8F';
const BLUE = '#3A86FF';

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold, rootMargin: '9999px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* rotating stamp badge */
const Stamp: React.FC<{ className?: string }> = ({ className }) => (
  <div className={className} style={{ width: 130, height: 130, animation: 'fSpin 14s linear infinite' }} aria-hidden="true">
    <svg viewBox="0 0 130 130" width="130" height="130">
      <defs>
        <path id="fCircle" d="M65,65 m-48,0 a48,48 0 1,1 96,0 a48,48 0 1,1 -96,0" />
      </defs>
      <circle cx="65" cy="65" r="63" fill={YELLOW} stroke={BLACK} strokeWidth="3" />
      <circle cx="65" cy="65" r="34" fill="none" stroke={BLACK} strokeWidth="2.5" />
      <text style={{ font: '800 13.5px Heebo, sans-serif', letterSpacing: 2 }} fill={BLACK}>
        <textPath href="#fCircle">7 ימים חינם ✦ בלי כרטיס אשראי ✦ ביטול בכל עת ✦</textPath>
      </text>
      <text x="65" y="72" textAnchor="middle" style={{ font: '900 20px Heebo, sans-serif' }} fill={BLACK}>חינם</text>
    </svg>
  </div>
);

/* sticker chip */
const Sticker: React.FC<{ bg: string; rotate?: number; children: React.ReactNode }> = ({ bg, rotate = 0, children }) => (
  <span
    className="inline-block px-4 py-2 font-bold text-sm sm:text-base"
    style={{ background: bg, color: BLACK, border: `3px solid ${BLACK}`, boxShadow: `4px 4px 0 ${BLACK}`, transform: `rotate(${rotate}deg)`, fontFamily: 'Heebo, sans-serif' }}
  >
    {children}
  </span>
);

/* polaroid with pin */
const Polaroid: React.FC<{ src: string; alt: string; caption: string; rotate: number; visible: boolean; delay: number }> = ({ src, alt, caption, rotate, visible, delay }) => (
  <figure
    className="f-polaroid relative bg-white p-3 pb-4"
    style={{
      border: `3px solid ${BLACK}`, boxShadow: `8px 8px 0 ${BLACK}`,
      transform: `rotate(${rotate}deg) translateY(${visible ? 0 : 40}px)`,
      opacity: visible ? 1 : 0,
      transition: `all 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
    }}
  >
    <span className="absolute -top-4 right-1/2 translate-x-1/2 text-3xl z-10" aria-hidden="true">📌</span>
    <img src={src} alt={alt} loading="lazy" className="w-full h-auto block" style={{ border: `2px solid ${BLACK}` }} />
    <figcaption className="pt-3 text-center font-bold text-lg" style={{ fontFamily: 'Heebo, sans-serif' }}>{caption}</figcaption>
  </figure>
);

const FEATURES = [
  { e: '💳', t: 'סליקה + קבלות', bg: '#B8F1B0' },
  { e: '🚀', t: 'קמפיינים עם AI', bg: '#FFD6E0' },
  { e: '👥', t: 'CRM לעמותות', bg: '#CDE7FF' },
  { e: '💬', t: 'דיוור / SMS / וואטסאפ', bg: '#FFF3B0' },
  { e: '📊', t: 'תקציב ודוחות', bg: '#E2CFFF' },
  { e: '🤝', t: 'מתנדבים ואירועים', bg: '#FFE0C2' },
];

const LandingVariantF: React.FC = () => {
  const shots = useReveal();
  const feats = useReveal();
  const cta = useReveal(0.2);

  return (
    <div dir="rtl" className="min-h-screen overflow-x-clip" style={{ background: YELLOW, color: BLACK }}>
      <style>{`
        @keyframes fSpin { to { transform: rotate(360deg); } }
        @keyframes fMarquee { from { transform: translateX(0); } to { transform: translateX(50%); } }
        @keyframes fPop { from { opacity: 0; transform: scale(0.85) rotate(-2deg); } to { opacity: 1; transform: scale(1) rotate(0deg); } }
        @keyframes fWiggle { 0%,100% { transform: rotate(-1.2deg); } 50% { transform: rotate(1.2deg); } }
        .f-pop { animation: fPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both; }
        .f-p1 { animation-delay: 120ms; } .f-p2 { animation-delay: 240ms; } .f-p3 { animation-delay: 360ms; }
        .f-btn { transition: transform 0.15s, box-shadow 0.15s; }
        .f-btn:hover { transform: translate(-3px, -3px); box-shadow: 9px 9px 0 ${BLACK} !important; }
        .f-btn:active { transform: translate(2px, 2px); box-shadow: 3px 3px 0 ${BLACK} !important; }
        .f-polaroid:hover { transform: rotate(0deg) scale(1.03) !important; z-index: 10; }
      `}</style>

      {/* ── poster header ── */}
      <header className="sticky top-0 z-40" style={{ background: YELLOW, borderBottom: `4px solid ${BLACK}` }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="text-4xl leading-none" style={{ fontFamily: POSTER }}>עמוטק!</Link>
          <div className="flex items-center gap-3" style={{ fontFamily: 'Heebo, sans-serif' }}>
            <Link to="/login" className="font-bold text-sm underline underline-offset-4 decoration-2">כניסה</Link>
            <Link to="/register" className="f-btn font-black text-sm px-4 py-2" style={{ background: PINK, border: `3px solid ${BLACK}`, boxShadow: `5px 5px 0 ${BLACK}` }}>
              התחל בחינם ←
            </Link>
          </div>
        </div>
      </header>

      {/* ━━━━ HERO ━━━━ */}
      <section className="relative px-5 pt-14 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="f-pop flex flex-wrap gap-3 mb-8">
            <Sticker bg="#fff" rotate={-2}>🇮🇱 מיוצר בישראל</Sticker>
            <Sticker bg={PINK} rotate={1.5}>חדש: AI שכותב קמפיינים</Sticker>
          </div>

          <h1 className="f-pop f-p1 leading-[0.95]" style={{ fontFamily: POSTER, fontSize: 'clamp(64px, 13vw, 180px)' }}>
            די לבלגן<span style={{ color: PINK, WebkitTextStroke: `3px ${BLACK}` }}>!</span>
            <br />
            <span style={{ WebkitTextStroke: `3px ${BLACK}`, color: YELLOW }}>עמותה מנהלים</span>
            {' '}
            <span className="inline-block px-4" style={{ background: BLACK, color: YELLOW, transform: 'rotate(-1.5deg)' }}>במקום אחד</span>
          </h1>

          <div className="f-pop f-p2 mt-8 grid lg:grid-cols-[1fr_auto] gap-8 items-end">
            <p className="text-xl sm:text-2xl font-bold leading-relaxed max-w-2xl" style={{ fontFamily: 'Heebo, sans-serif' }}>
              תרומות, קמפיינים, CRM, דיוור ותקציב — מערכת אחת ישראלית שעושה סדר. בלי שבע מערכות. בלי אקסלים. בלי כאב ראש.
            </p>
            <Stamp className="hidden lg:block flex-shrink-0" />
          </div>

          <div className="f-pop f-p3 mt-8 flex flex-wrap items-center gap-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            <Link to="/register" className="f-btn inline-block px-9 py-4 text-xl font-black" style={{ background: BLUE, color: '#fff', border: `3px solid ${BLACK}`, boxShadow: `6px 6px 0 ${BLACK}` }}>
              פתח חשבון בחינם ←
            </Link>
            <Link to="/landing#pricing" className="f-btn inline-block px-6 py-4 text-lg font-bold" style={{ background: '#fff', border: `3px solid ${BLACK}`, boxShadow: `6px 6px 0 ${BLACK}` }}>
              כמה זה עולה?
            </Link>
          </div>
        </div>
      </section>

      {/* ━━━━ MARQUEE ━━━━ */}
      <div className="py-3 overflow-hidden select-none" style={{ background: BLACK }} dir="ltr">
        <div className="flex w-max whitespace-nowrap" style={{ animation: 'fMarquee 22s linear infinite' }}>
          {Array.from({ length: 2 }).flatMap((_, r) =>
            ['תרומות', 'קמפיינים', 'CRM', 'וואטסאפ', 'תקציב', 'דוחות', 'AI', 'מתנדבים'].map((w, i) => (
              <span key={`${r}-${i}`} className="mx-3 text-2xl" style={{ fontFamily: POSTER, color: i % 2 ? YELLOW : '#fff' }}>
                ★ {w}
              </span>
            ))
          )}
        </div>
      </div>

      {/* ━━━━ PINNED POLAROIDS — real screenshots ━━━━ */}
      <section ref={shots.ref} className="px-5 py-20" style={{ background: '#FFF8DC' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="mb-2" style={{ fontFamily: POSTER, fontSize: 'clamp(44px, 7vw, 90px)', lineHeight: 1 }}>
            הוכחות<span style={{ color: BLUE }}>.</span> לא הבטחות<span style={{ color: PINK }}>.</span>
          </h2>
          <p className="mb-12 text-xl font-bold" style={{ fontFamily: 'Heebo, sans-serif' }}>
            צילומי מסך אמיתיים מתוך המערכת — נעצו על הלוח. 📌
          </p>

          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div className="lg:col-span-2 max-w-3xl mx-auto w-full" style={{ animation: shots.visible ? 'fWiggle 6s ease-in-out infinite' : 'none' }}>
              <Polaroid src="/screenshots/income-hero.png" alt="מסך הכנסות ותרומות — עמוטק" caption="₪307,547 תרומות. מסך אחד. אפס אקסלים." rotate={-1.5} visible={shots.visible} delay={0} />
            </div>
            <Polaroid src="/screenshots/budget.png" alt="לוח תקציב שנתי — עמוטק" caption="תקציב שנתי: תכנון מול ביצוע 📊" rotate={2} visible={shots.visible} delay={200} />
            <Polaroid src="/screenshots/money-flow.png" alt="תנועת כספים — עמוטק" caption="תנועת כספים — 90 יום אחורה 📈" rotate={-2.5} visible={shots.visible} delay={350} />
          </div>

          {/* mini polaroids — telephony, mailing, calendar */}
          <div className="grid sm:grid-cols-3 gap-8 mt-14">
            {[
              { src: '/screenshots/mail-builder.png', alt: 'בונה הדיוור — עמוטק', cap: '💌 בונה דיוור בגרירה', r: -2 },
              { src: '/screenshots/telephony.png', alt: 'מוקד טלפוני — עמוטק', cap: '📞 מוקד טלפוני + AI', r: 2.2 },
              { src: '/screenshots/calendar.png', alt: 'לוח שנה — עמוטק', cap: '🗓️ לוח שנה עברי-לועזי', r: -1.6 },
            ].map((s, i) => (
              <figure
                key={s.src}
                className="f-polaroid relative bg-white p-2.5 pb-3"
                style={{
                  border: `3px solid ${BLACK}`, boxShadow: `6px 6px 0 ${BLACK}`,
                  transform: `rotate(${s.r}deg) translateY(${shots.visible ? 0 : 40}px)`,
                  opacity: shots.visible ? 1 : 0,
                  transition: `all 0.7s cubic-bezier(0.22,1,0.36,1) ${450 + i * 150}ms`,
                }}
              >
                <span className="absolute -top-4 right-1/2 translate-x-1/2 text-2xl z-10" aria-hidden="true">📌</span>
                <div style={{ height: 140, overflow: 'hidden', border: `2px solid ${BLACK}` }}>
                  <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-full block object-cover object-top" />
                </div>
                <figcaption className="pt-2.5 text-center font-bold text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>{s.cap}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━ FEATURE STICKERS ━━━━ */}
      <section ref={feats.ref} className="px-5 py-20" style={{ background: PINK, borderTop: `4px solid ${BLACK}`, borderBottom: `4px solid ${BLACK}` }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="mb-10" style={{ fontFamily: POSTER, fontSize: 'clamp(40px, 6vw, 76px)', lineHeight: 1 }}>
            מה יש בפנים?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.t}
                className="p-6 font-bold text-xl flex items-center gap-4"
                style={{
                  fontFamily: 'Heebo, sans-serif', background: f.bg, border: `3px solid ${BLACK}`,
                  boxShadow: `6px 6px 0 ${BLACK}`, transform: `rotate(${i % 2 ? 0.8 : -0.8}deg) translateY(${feats.visible ? 0 : 30}px)`,
                  opacity: feats.visible ? 1 : 0, transition: `all 0.6s cubic-bezier(0.34,1.56,0.64,1) ${i * 90}ms`,
                }}
              >
                <span className="text-4xl">{f.e}</span>
                {f.t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━ CTA ━━━━ */}
      <section ref={cta.ref} className="px-5 py-24 text-center" style={{ background: BLACK, color: YELLOW }}>
        <div className={`max-w-4xl mx-auto transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 style={{ fontFamily: POSTER, fontSize: 'clamp(52px, 9vw, 120px)', lineHeight: 0.95 }}>
            נו, מתחילים?
          </h2>
          <p className="mt-5 text-xl font-bold" style={{ fontFamily: 'Heebo, sans-serif', color: '#fff' }}>
            7 ימי ניסיון חינם · בלי כרטיס אשראי · ביטול בכל עת
          </p>
          <Link
            to="/register"
            className="f-btn inline-block mt-10 px-12 py-5 text-2xl font-black"
            style={{ fontFamily: 'Heebo, sans-serif', background: YELLOW, color: BLACK, border: `3px solid ${YELLOW}`, boxShadow: '6px 6px 0 #fff' }}
          >
            פתח חשבון בחינם ←
          </Link>
        </div>
      </section>

      <footer className="px-5 py-6 text-sm font-bold flex flex-col sm:flex-row items-center justify-between gap-2 max-w-6xl mx-auto" style={{ fontFamily: 'Heebo, sans-serif' }}>
        <p>© 2026 עמוטק · Y.Sh כלכלה וצריכה חברתית בע״מ</p>
        <div className="flex gap-4">
          <Link to="/terms" className="underline underline-offset-4">תנאי שימוש</Link>
          <Link to="/privacy" className="underline underline-offset-4">פרטיות</Link>
          <Link to="/accessibility" className="underline underline-offset-4">נגישות</Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingVariantF;
